/* eslint-disable react/jsx-no-duplicate-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import FormControl from '@material-ui/core/FormControl';
import { SelectTypeaheadAtom, SelectTypeaheadAtomProps } from '../atoms/SelectTypeahead/SelectTypeaheadAtom';
import { getSelectedOptions } from '../atoms/SelectTypeahead/parts/getSelectedOptions';
import { TypeaheadOption } from '../atoms/SelectTypeahead/parts/types';
import { FieldValue } from '../types';
import { MatchHintSlot } from './MatchHintSlot';

export interface SelectTypeaheadProps<OptionValue, TargetModel extends any = OptionValue>
  extends Omit<
    SelectTypeaheadAtomProps<OptionValue, TargetModel>,
    'errors' | 'error' | 'touched' | 'onTouched' | 'onSetFieldValue' | 'value'
  > {
  isEqual?: (optionValue: OptionValue, selectedOptionValue: OptionValue) => boolean;
  mapInputValue?: (value?: TargetModel) => OptionValue | undefined;
  // opt-in fn which allows "creating" missing dropdown options (component cannot find a dropdown option with that value)
  // e.g. model: { uid: 3 }
  // but you provided dropdown options just for uids: 1,2 ( uid "3" might be deleted or will come later in some case: e.g. pagination)
  // By default value without match is removed on blur event but thanks to that
  // this value is persisted in model and user will show any message you want
  onMissingOption?: (value: OptionValue) => TypeaheadOption<OptionValue>;
  initialValue?: string;
  onSetFieldValue?: SelectTypeaheadAtomProps<OptionValue, TargetModel>['onSetFieldValue'];
  recentItems?: TypeaheadOption<FieldValue>[];
}

export const SelectTypeahead = <OptionValue extends any = string, TargetModel extends any = OptionValue>({
  name,
  required,
  label,
  helpText,
  infoText,
  placeholder,
  fullWidth,
  values,
  mapInputValue,
  initialValue,
  isEqual,
  onMissingOption,
  mapOutputValue,
  nullifyNoneOption,
  onSetFieldValue,
  recentItems,
  ...props
}: SelectTypeaheadProps<OptionValue, TargetModel>) => {
  const [input, meta, helpers] = useField<TargetModel | undefined | null>(name || 'INVALID');
  const formikContext = useFormikContext();
  const [isInitialValueSet, setIsInitialValueSet] = useState(false);
  const valueSetter = useMemo(() => onSetFieldValue ?? helpers.setValue, [onSetFieldValue, helpers.setValue]);
  const onChange = useCallback(
    (model: TargetModel | undefined | null) => {
      valueSetter(model);
    },
    [valueSetter]
  );

  const errorText = isErrorObject(formikContext.errors) ? formikContext.errors.flat[name] : meta.error;

  // Get the selected options
  const selectedOptions = useMemo(
    () =>
      getSelectedOptions(
        input.value,
        values || [],
        false,
        isInitialValueSet,
        mapInputValue,
        initialValue,
        isEqual,
        onMissingOption
      ),
    [initialValue, isInitialValueSet, mapInputValue, input.value, values, isEqual, onMissingOption]
  );

  const numValues = values?.length;

  // Set initial value if its specified. getSelectedOptions already protects an already-hydrated
  // saved value from being clobbered (it only resolves the configured initialValue when the
  // field's current value didn't match a real option), so selectedOptions here reflects that.
  useEffect(() => {
    const canSet = !isInitialValueSet && !!initialValue && !!numValues;
    if (canSet) {
      if (selectedOptions?.length) {
        const option = selectedOptions[0];
        const targetModel = mapOutputValue
          ? (mapOutputValue(option?.value) as TargetModel)
          : (option?.value as TargetModel);
        valueSetter(targetModel || (nullifyNoneOption ? null : undefined));
      } else {
        valueSetter(nullifyNoneOption ? null : undefined);
      }
      setIsInitialValueSet(true);
    }
  }, [isInitialValueSet, initialValue, mapOutputValue, nullifyNoneOption, numValues, selectedOptions, valueSetter]);

  const hasSubmitted = formikContext.submitCount > 0;

  return (
    <FormControl error={!!errorText && !!meta.touched} required={required} fullWidth={fullWidth}>
      <MatchHintSlot name={name}>
        <SelectTypeaheadAtom<OptionValue, TargetModel>
          name={name}
          label={label}
          placeholder={placeholder}
          key={name}
          fullWidth={fullWidth}
          selected={selectedOptions}
          helpText={hasSubmitted && errorText ? errorText : helpText}
          infoText={infoText}
          required={required}
          error={hasSubmitted && errorText ? errorText : ''}
          onSetFieldValue={onChange}
          value={input.value}
          values={values}
          mapOutputValue={mapOutputValue}
          nullifyNoneOption={nullifyNoneOption}
          recentItems={recentItems}
          {...props}
        />
      </MatchHintSlot>
    </FormControl>
  );
};

function isErrorObject(value: any): value is Record<string, string> & { flat: Record<string, string> } {
  return !!value && 'flat' in value && typeof value === 'object';
}
